import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of, throwError, EMPTY } from 'rxjs';
import { take, catchError, finalize, shareReplay, mergeMap } from 'rxjs/operators';
import { AccessControlService, ScreenState } from 'app/services/general/access-control/access-control.service';

export type FieldState = 'full' | 'hidden';

/**
 * Nó da árvore de permissões (novo shape — backend Fase C).
 * A hierarquia é construída recursivamente via parent_key (N níveis):
 * módulo (layer='access') → aba (layer='tab') → sub-aba (layer='tab').
 * Fields (layer='field') NÃO entram na árvore — são expostos à parte (flat map).
 */
export interface PermissionNode {
    screenKey: string;
    label: string;
    layer: 'access' | 'tab' | 'field' | 'header_action' | string;
    state: FieldState;
    /** Apenas em layer='access'. undefined/null = sem toggle de concessão. */
    contextGranted?: boolean | null;
    /** Apenas em layer='header_action' (ex: "Menu do Usuário"). */
    group?: string | null;
    /** Map screen_key → nó filho (vazio quando não há filhos). */
    children?: Record<string, PermissionNode>;
}

@Injectable({
    providedIn: 'root'
})
export class TabPermissionService
{
    private _flat: { [screenKey: string]: FieldState } = {};
    private _hierarchy: { [moduleKey: string]: PermissionNode } = {};
    /** Concessão de contexto por módulo (toggle "Concessão de Contexto" da
     *  matriz): capturada do `contextGranted` dos nós layer='access' da
     *  permissionsTree (/navigation/menu) e da hierarchy do login. O backend
     *  entrega o valor REAL: true por ausência de linha (default ON), false
     *  quando granted=0 (toggle OFF). undefined (não mapeado por este serviço)
     *  resolve para true pelo default implicito da RN-CTX-001. */
    private _contextGrants: { [moduleKey: string]: boolean } = {};
    private _loaded = false;
    /** true quando o flat veio do servidor (/screen-permissions/me) nesta sessão.
     *  O cache do localStorage/menu NÃO marca esta flag: a primeira chamada de
     *  loadPermissions() da sessão SEMPRE revalida via servidor, curando caches
     *  desatualizados quando o backend adiciona screen_keys novas.
     *  IMPORTANTE (2026-08-18): só é marcado em resposta BEM-SUCEDIDA — erro
     *  não-401 NÃO marca (a próxima chamada tenta revalidar de novo). */
    private _serverValidated = false;
    /** Momento (Date.now) da última revalidação bem-sucedida via servidor.
     *  Usado pelo TTL de revalidação intrasessão: mudanças de permissão feitas
     *  pelo admin passam a valer para usuário logado sem re-login em até 5 min
     *  (fecha o cenário "aba habilitada mas usuário continua expulso"). */
    private _loadedAt = 0;
    /** Janela de validade do cache validado por servidor (revalidação intrasessão). */
    private static readonly CACHE_TTL_MS = 5 * 60 * 1000;
    /** Mapa aba (layer='tab') → screen_keys das sub-abas diretas (filhos não-field),
     *  capturado da árvore do menu (permissionsTree) e da hierarchy do storage.
     *  Base da derivação bottom-up no flat, UNIVERSAL desde 2026-08-18: TODA aba
     *  com filhos tab deriva para hidden quando TODAS as sub-abas estão hidden
     *  (o backend removeu a whitelist PURE_CONTAINER_TABS — antiga Lacuna 1
     *  encerrada; conta-accounts/grp-groups-list/rct-unified derivam).
     *
     *  NÃO captura módulos (layer='access'): a derivação aba→módulo é feita pelo
     *  BACKEND no /navigation/menu (não-recursiva por estado das abas diretas) —
     *  o cliente apenas respeita o menu derivado; a flat do cliente alimenta as
     *  tab bars de ABAS e os guards, que não derivam módulos. */
    private _containerChildren: { [containerKey: string]: string[] } = {};
    private _loading$ = new BehaviorSubject<boolean>(false);
    /** Observable compartilhado do request em voo (dedup de loadPermissions). */
    private _pending: Observable<any> | null = null;
    /** Época do cache: incrementada em reset() — respostas HTTP em voo de uma
     *  época anterior (ex.: sessão A) são DESCARTADAS quando completam depois
     *  do reset (nunca repopulam o cache com dados do usuário anterior). */
    private _epoch = 0;

    constructor(private _accessControl: AccessControlService)
    {
        this._tryLoadFromStorage();
    }

    private _tryLoadFromStorage(): void
    {
        try
        {
            // Preferência 1: flat map fresco do /screen-permissions/me (chave dedicada)
            const storedFlat = localStorage.getItem('permissions_flat');
            if (storedFlat)
            {
                const flat = JSON.parse(storedFlat);
                if (flat && typeof flat === 'object')
                {
                    this._flat = flat;
                    this._loaded = true;
                    return;
                }
            }

            // Preferência 2 (fallback): hierarchy aninhada salva no login (chave 'permissions')
            const stored = localStorage.getItem('permissions');
            if (stored)
            {
                const hierarchy = JSON.parse(stored);
                this._hierarchy = hierarchy;
                this._flattenHierarchy(hierarchy);
                this._loaded = true;
                this._captureContextGrants(hierarchy);
                this._captureContainerChildren(hierarchy);
                this._applyDerivedContainerStates();
            }
        }
        catch (e)
        {
        }
    }

    private _persistFlatToStorage(flat: { [screenKey: string]: FieldState }): void
    {
        try
        {
            localStorage.setItem('permissions_flat', JSON.stringify(flat));
        }
        catch (e)
        {
        }
    }

    private _flattenHierarchy(hierarchy: any): void
    {
        // Fallback: árvore recursiva salva no login (chave 'permissions').
        // Sem cascade — espelha os estados que o backend computou.
        this._flat = this._walkNodes(hierarchy);
    }

    /**
     * Caminha a árvore recursiva (novo shape) e produz o flat map screen_key → state.
     * @param tree Map raiz: screen_key → PermissionNode (login, menu permissionsTree,
     *             ou endpoint de gestão de permissões).
     * O cascade de hidden aplica-se aos descendentes de nós hidden (dentro de _walkNode).
     */
    private _walkNodes(tree: any): { [screenKey: string]: FieldState }
    {
        const flat: { [screenKey: string]: FieldState } = {};

        if (!tree || typeof tree !== 'object')
        {
            return flat;
        }

        for (const key of Object.keys(tree))
        {
            // IMPORTANTE: nós RAIZ nunca herdam estado — o estado de cada nó raiz é
            // respeitado (ex.: permissionsTree do menu com state='full'). Antes, o
            // cascade era aplicado também às raízes, forçando TODOS os módulos a
            // 'hidden' (bug 2026-08-14: o menu nunca concedia acesso via
            // permissionsTree — só era mascarado pelo merge posterior do
            // /screen-permissions/me).
            this._walkNode(tree[key], flat, null);
        }

        return flat;
    }

    private _walkNode(node: any, flat: { [screenKey: string]: FieldState }, inheritedState: FieldState | null): void
    {
        if (!node || typeof node !== 'object')
        {
            return;
        }

        const screenKey = node.screenKey ?? node.access_key ?? node.tab_key ?? node.key;
        if (!screenKey)
        {
            return;
        }

        let state = this._normalizeState(node.state ?? node.show);
        if (inheritedState === 'hidden')
        {
            state = 'hidden';
        }
        flat[screenKey] = state;

        const children = node.children;
        if (children && typeof children === 'object')
        {
            for (const childKey of Object.keys(children))
            {
                this._walkNode(children[childKey], flat, state === 'hidden' ? 'hidden' : null);
            }
        }
    }

    setPermissionsFromTree(tree: any): void
    {
        if (!tree || typeof tree !== 'object')
        {
            return;
        }

        // MERGE não-destrutivo: a árvore do menu é um SUBSET do catálogo
        // (apenas módulos/tabs visíveis no menu). Substituir o flat completo
        // por este subset destruiria keys legítimas (ex: fields) já carregadas
        // do /screen-permissions/me — bug de abas sumindo (2026-08-13).
        this._captureContextGrants(tree);
        this._captureContainerChildren(tree);
        this._flat = { ...this._flat, ...this._flattenTree(tree) };
        this._loaded = true;
        this._applyDerivedContainerStates();
        this._persistFlatToStorage(this._flat);
    }

    /**
     * Captura a CONCESSÃO DE CONTEXTO por módulo (toggle "Concessão de
     * Contexto" da matriz) a partir dos nós layer='access' da árvore
     * (permissionsTree do /navigation/menu ou hierarchy do storage). O campo
     * `contextGranted` do nó traz o valor REAL do backend (true por ausência
     * de linha — default ON, RN-CTX-001; false quando granted=0 — toggle OFF).
     * Só captura valores booleanos explícitos; nó sem o campo mantém o default
     * ON na leitura (isContextGranted).
     */
    private _captureContextGrants(tree: any): void
    {
        const walk = (node: any): void =>
        {
            if (!node || typeof node !== 'object')
            {
                return;
            }

            const screenKey = node.screenKey ?? node.access_key ?? node.tab_key ?? node.key;
            if (screenKey && typeof node.contextGranted === 'boolean')
            {
                this._contextGrants[screenKey] = node.contextGranted;
            }

            const children = node.children;
            if (children && typeof children === 'object')
            {
                for (const childKey of Object.keys(children))
                {
                    walk(children[childKey]);
                }
            }
        };

        for (const rootKey of Object.keys(tree))
        {
            walk(tree[rootKey]);
        }
    }

    /**
     * Captura a hierarquia "aba-container → sub-abas" da árvore de permissões
     * (permissionsTree do /navigation/menu ou hierarchy do storage). A árvore
     * tem a ESTRUTURA de parentesco completa (nós aninhados), mesmo com os
     * estados brutos — é a fonte da derivação bottom-up no flat.
     *
     * UNIVERSAL desde 2026-08-18: captura TODA aba (layer='tab') com filhos
     * (a whitelist CONTAINER_TAB_KEYS foi removida — o backend derivou
     * universalmente e o cliente espelha: conta-accounts, grp-groups-list e
     * rct-unified participam da derivação). Módulos (layer='access') não
     * entram: a derivação aba→módulo é exclusiva do menu no backend.
     */
    private _captureContainerChildren(tree: any): void
    {
        const walk = (node: any): void =>
        {
            if (!node || typeof node !== 'object')
            {
                return;
            }

            const screenKey = node.screenKey ?? node.access_key ?? node.tab_key ?? node.key;
            const layer = node.layer ?? node.type;
            const children = node.children;

            if (screenKey
                && layer === 'tab'
                && children
                && typeof children === 'object'
                && this._containerChildren[screenKey] === undefined)
            {
                // Sub-abas = filhos não-field (fields são expostos à parte no
                // flat e NÃO participam da derivação de visibilidade).
                const childKeys = Object.values(children)
                    .filter((c: any) => c && typeof c === 'object' && (c.layer ?? c.type) !== 'field')
                    .map((c: any) => c?.screenKey ?? c?.access_key ?? c?.tab_key ?? c?.key)
                    .filter((k: unknown): k is string => typeof k === 'string');

                if (childKeys.length > 0)
                {
                    this._containerChildren[screenKey] = childKeys;
                }
            }

            if (children && typeof children === 'object')
            {
                for (const childKey of Object.keys(children))
                {
                    walk(children[childKey]);
                }
            }
        };

        for (const rootKey of Object.keys(tree))
        {
            walk(tree[rootKey]);
        }
    }

    /**
     * Derivação bottom-up no FLAT (Ex.3 — consistência menu × tab bar):
     * aba com sub-abas (layer='tab' + filhos) com TODAS as sub-abas hidden no
     * flat → a própria aba vira hidden. UNIVERSAL desde 2026-08-18: aplica a
     * TODAS as abas com filhos tab (o backend removeu a whitelist do
     * PURE_CONTAINER_TABS — a antiga "Lacuna 1", que preservava abas com
     * conteúdo próprio, foi encerrada; conta-accounts e grp-groups-list
     * derivam como qualquer outra). É a MESMA regra que o backend aplica no
     * menu (GetNavigationMenuAction::derivedState), replicada no cliente
     * porque a tab bar de abas dos módulos consome este flat (estados brutos
     * do /screen-permissions/me; a permissionsTree do menu NÃO traz o estado
     * derivado). Sem isto, a aba container continuaria clicável na tab bar do
     * módulo, mas o FirstAccessibleTabGuard interno cairia em /home (nenhuma
     * sub-aba habilitada) — inconsistência visual/navegacional.
     */
    private _applyDerivedContainerStates(): void
    {
        for (const containerKey of Object.keys(this._containerChildren))
        {
            const children = this._containerChildren[containerKey];
            if (!children || children.length === 0)
            {
                continue;
            }

            // Acesso DIRETO ao flat (não getState): a derivação roda também em
            // transições onde _loaded ainda não foi marcado — getState retornaria
            // 'hidden' para tudo e rebaixaria TODOS os containers por engano.
            const allChildrenHidden = children.every((childKey) => (this._flat[childKey] ?? 'hidden') === 'hidden');
            if (allChildrenHidden)
            {
                this._flat[containerKey] = 'hidden';
            }
        }
    }

    private _flattenTree(tree: any): { [screenKey: string]: FieldState }
    {
        // Árvore do menu (permissionsTree): cascade de hidden para descendentes,
        // mantendo a semântica do flatten anterior.
        return this._walkNodes(tree);
    }

    private _normalizeState(value: any): FieldState
    {
        if (value === 'full' || value === 'hidden')
        {
            return value;
        }
        return 'hidden';
    }

    loadPermissions(force: boolean = false): Observable<any>
    {
        // Cache completo do servidor nesta sessão: usa direto (sem request),
        // desde que ainda dentro do TTL de revalidação intrasessão. Fora do
        // TTL (ou force), revalida — mudança de permissão feita pelo admin
        // passa a valer para usuário logado sem re-login em até CACHE_TTL_MS.
        const cacheExpired = this._serverValidated && (Date.now() - this._loadedAt) > TabPermissionService.CACHE_TTL_MS;
        if (this._loaded && !force && this._serverValidated && !cacheExpired)
        {
            return of({ permissions: this._flat });
        }

        // Request em voo: compartilha o mesmo observable (evita N requests
        // quando vários componentes chamam loadPermissions no mesmo tick).
        // shareReplay: o chain é caloroso após o 1º subscriber — N subscribers
        // simultâneos compartilham UMA execução da fonte (dedup efetivo).
        if (this._pending)
        {
            return this._pending;
        }

        this._loading$.next(true);

        const epoch = this._epoch;
        const pending = this._accessControl.getMyPermissions().pipe(
            take(1),
            mergeMap((response) =>
            {
                // Epoch guard: resposta de época anterior (reset() durante o voo)
                // é DESCARTADA — nunca repopula o cache com dados do usuário/
                // sessão anterior (race in-flight × reset, H1 review 2.1).
                if (epoch !== this._epoch)
                {
                    this._loading$.next(false);
                    return EMPTY;
                }

                const fresh = response.permissions || {};
                this._flat = { ...this._flat, ...fresh };
                this._loaded = true;
                // Reaplica a derivação bottom-up DEPOIS do merge: o flat bruto
                // (/screen-permissions/me) sobrescreve o estado derivado da
                // árvore do menu — sem esta passada, a aba-container com todas
                // as sub-abas hidden voltaria a ficar "full" e clicável na tab
                // bar do módulo (Ex.3).
                this._applyDerivedContainerStates();
                this._serverValidated = true;
                this._loadedAt = Date.now();
                this._loading$.next(false);
                this._persistFlatToStorage(this._flat);
                return of({ permissions: this._flat });
            }),
            catchError((error) =>
            {
                if (error?.status === 401)
                {
                    this._loading$.next(false);
                    return throwError(() => error);
                }
                // Erro não-401: PRESERVA o cache conhecido (storage/menu) em vez
                // de esvaziar o flat — fail-open da RESPOSTA. Porém NÃO marca
                // _serverValidated: a política de cache continua exigindo
                // revalidação na próxima chamada (evita congelar a sessão em
                // um flat parcial por erro transitório de rede/5xx).
                this._loading$.next(false);
                return of({ permissions: this._flat });
            }),
            finalize(() =>
            {
                // Só limpa o dedup se a chain que completou é a corrente — evita
                // que uma chain antiga (epoch anterior) zere o _pending da nova.
                if (this._pending === pending)
                {
                    this._pending = null;
                }
            }),
            shareReplay({ bufferSize: 1, refCount: false })
        );
        this._pending = pending;

        return pending;
    }

    canAccess(screenKey: string): boolean
    {
        return this.getState(screenKey) === 'full';
    }

    getState(screenKey: string): FieldState
    {
        if (!this._loaded)
        {
            return 'hidden';
        }

        return this._flat[screenKey] ?? 'hidden';
    }

    isFull(screenKey: string): boolean
    {
        return this.getState(screenKey) === 'full';
    }

    /**
     * O perfil TEM o contexto do módulo (toggle "Concessão de Contexto")?
     * Decisão para WIDGETS que consomem endpoints de abas/módulos-fonte
     * (regra do usuário 2026-08-18): o toggle de contexto rege o acesso
     * cruzado — mesmo com o módulo-fonte desabilitado na UI (hidden), o
     * widget funciona via contexto (Cenário 6).
     *
     * Valor do `contextGranted` capturado da permissionsTree (/navigation/menu)
     * e da hierarchy do login — o backend entrega o valor REAL: true por
     * ausência de linha (default ON, RN-CTX-001), false quando granted=0.
     * Ausência do campo no nó (mocks/storage legados) resolve para o default
     * implícito ON.
     */
    isContextGranted(moduleKey: string): boolean
    {
        const granted = this._contextGrants[moduleKey];
        return granted === undefined ? true : granted;
    }

    get hierarchy(): { [moduleKey: string]: PermissionNode }
    {
        return this._hierarchy;
    }

    get loading$(): Observable<boolean>
    {
        return this._loading$.asObservable();
    }

    get loaded(): boolean
    {
        return this._loaded;
    }

    reset(): void
    {
        this._epoch++;
        this._flat = {};
        this._hierarchy = {};
        this._contextGrants = {};
        this._containerChildren = {};
        this._loaded = false;
        this._serverValidated = false;
        this._loadedAt = 0;
        this._pending = null;
        localStorage.removeItem('permissions');
        localStorage.removeItem('permissions_flat');
    }
}
